import { ClientUser, UserRole } from '@numeo/types-core'
import { TeamDriverRow, TeamMemberRow, TeamPerson } from '@numeo/types-main-web-app'

/**
 * Team directory rows built from the roster `/auth/verify` carries (`application.users`), for a company the directory
 * does not serve yet (`featureFlags.teamDirectory` off). They are the rows `/team/*` serves, so a screen reads the same
 * shape at both stages. Pure.
 */

const iso = (value: Date | string | null | undefined): string | undefined => {
    if (!value) return undefined
    const date = value instanceof Date ? value : new Date(value)
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

/** A roster entry that is a company member (admin, dispatcher, accountant): the directory's members list. */
export const isMemberUser = (user: Pick<ClientUser, 'role'>): boolean => user.role !== UserRole.DRIVER

/** A member's row with every tier: the roster carries all of it today. */
export const memberRowFromUser = (user: ClientUser, ownerId: string | undefined): TeamMemberRow => {
    const linked = (user.nylasEmails ?? []).filter((mailbox) => !!mailbox.email)
    const mailboxes = linked.map((mailbox) => mailbox.email)
    const primaryMailbox = linked.find((mailbox) => mailbox.isPrimary)?.email
    const lastSignedIn = iso(user.lastSignedIn)
    const joinedAt = iso(user.createdAt)
    return {
        id: String(user.id),
        email: user.email,
        role: user.role,
        isOwner: !!ownerId && String(user.id) === String(ownerId),
        mailboxConnected: linked.length > 0,
        ...(user.name ? { name: user.name } : {}),
        ...(user.rbacRoleId ? { rbacRoleId: user.rbacRoleId } : {}),
        ...(user.boxTruckAccess !== undefined ? { boxTruckAccess: user.boxTruckAccess } : {}),
        ...(typeof user.dispatchFee === 'number' ? { dispatchFee: user.dispatchFee } : {}),
        ...(mailboxes.length ? { mailboxes } : {}),
        ...(primaryMailbox ? { primaryMailbox } : {}),
        ...(user.telegramGroup?.chatId ? { telegramGroup: { chatId: user.telegramGroup.chatId, groupName: user.telegramGroup.groupName } } : {}),
        ...(joinedAt ? { joinedAt } : {}),
        ...(lastSignedIn ? { lastSignedIn } : {}),
    }
}

/** A driver's row with every tier (base and pay): the roster carries all of it today. */
export const driverRowFromUser = (user: ClientUser): TeamDriverRow => {
    const contract = user.contractSettings
    const terminationDate = iso(user.terminationDate)
    const lastInvitationSentAt = iso(user.lastInvitationSentAt)
    const createdAt = iso(user.createdAt)
    return {
        id: String(user.id),
        telegramConnected: !!user.telegramGroup?.chatId,
        hazmatEndorsed: !!user.cdlData?.permits?.hazmat,
        ...(user.name ? { name: user.name } : {}),
        ...(user.email ? { email: user.email } : {}),
        ...(user.phone ? { phone: user.phone } : {}),
        ...(user.truckId ? { truckId: String(user.truckId) } : {}),
        ...(user.teamSettings?.coDriverId ? { coDriverId: user.teamSettings.coDriverId } : {}),
        ...(user.employmentStatus ? { employmentStatus: user.employmentStatus } : {}),
        ...(terminationDate ? { terminationDate } : {}),
        ...(user.label ? { label: user.label } : {}),
        ...(user.eldCompany ? { eldCompany: user.eldCompany } : {}),
        ...(user.experienceInYears !== undefined ? { experienceInYears: user.experienceInYears } : {}),
        ...(user.isRegisteredInApp !== undefined ? { isRegisteredInApp: user.isRegisteredInApp } : {}),
        ...(lastInvitationSentAt ? { lastInvitationSentAt } : {}),
        ...(user.telegramGroup?.groupName ? { telegramGroupName: user.telegramGroup.groupName } : {}),
        ...(createdAt ? { createdAt } : {}),
        ...(contract
            ? {
                  contract: {
                      driverRole: contract.driverRole,
                      driverType: contract.driverType,
                      paymentType: contract.paymentType,
                      equipmentPayEnabled: contract.equipmentPayEnabled,
                      equipmentPayRules: contract.equipmentPayRules,
                  },
              }
            : {}),
        ...(user.compensation ? { compensation: user.compensation } : {}),
        ...(user.statementReportOverrides ? { statementReportOverrides: user.statementReportOverrides } : {}),
        ...(user.address ? { address: user.address } : {}),
        ...(user.city ? { city: user.city } : {}),
        ...(user.state ? { state: user.state } : {}),
        ...(user.zipCode ? { zipCode: user.zipCode } : {}),
        ...(user.driverCompany?.name || user.driverCompany?.address
            ? {
                  driverCompany: {
                      ...(user.driverCompany.name ? { name: user.driverCompany.name } : {}),
                      ...(user.driverCompany.address ? { address: user.driverCompany.address } : {}),
                  },
              }
            : {}),
    }
}

/** The fields of `row` named in `keys` that hold a value. */
const pickSet = <T extends object, K extends keyof T>(row: T, keys: readonly K[]): Partial<T> => {
    const picked: Partial<T> = {}
    for (const key of keys) if (row[key] !== undefined) picked[key] = row[key]
    return picked
}

const MEMBER_MANAGE_TIER = ['boxTruckAccess', 'dispatchFee', 'mailboxes', 'primaryMailbox', 'telegramGroup', 'joinedAt', 'lastSignedIn'] as const
const DRIVER_PAY_TIER = ['compensation', 'statementReportOverrides', 'address', 'city', 'state', 'zipCode', 'driverCompany'] as const

/** The manage-tier fields of a cached member row: what the base row of a `team.peopleChanged` upsert cannot replace. */
export const memberWiderTier = (cached: TeamMemberRow): Partial<TeamMemberRow> => pickSet(cached, MEMBER_MANAGE_TIER)

/** The pay-tier fields of a cached driver row, the contract's equipment pay among them, laid over the base row's contract. */
export const driverWiderTier = (cached: TeamDriverRow, row: TeamDriverRow): Partial<TeamDriverRow> => {
    const pay = pickSet(cached, DRIVER_PAY_TIER)
    const equipmentPay = cached.contract ? pickSet(cached.contract, ['equipmentPayEnabled', 'equipmentPayRules'] as const) : {}
    return Object.keys(equipmentPay).length ? { ...pay, contract: { ...row.contract, ...equipmentPay } } : pay
}

/** The name a person goes by: their name, else the part of their address before the `@` (the server's rule). */
const displayName = (person: { name?: string; email?: string | null }): string | undefined => person.name || person.email?.split('@')[0] || undefined

/** A roster entry as a person to name; null when there is no name to show. */
export const personFromUser = (user: ClientUser): TeamPerson | null => {
    const name = displayName(user)
    if (!name) return null
    return { id: String(user.id), name, kind: user.role === UserRole.DRIVER ? 'driver' : 'member', role: user.role }
}

/** A members-list row as a person to name. */
export const personFromMemberRow = (row: TeamMemberRow): TeamPerson | null => {
    const name = displayName(row)
    if (!name) return null
    return { id: row.id, name, kind: 'member', role: row.role, ...(row.avatar ? { avatar: row.avatar } : {}) }
}

/** A drivers-list row as a person to name. */
export const personFromDriverRow = (row: TeamDriverRow): TeamPerson | null => {
    const name = displayName(row)
    if (!name) return null
    return { id: row.id, name, kind: 'driver', role: UserRole.DRIVER, ...(row.avatar ? { avatar: row.avatar } : {}) }
}

/** The name to show for `id`, or `fallback` when nobody by that id is known. */
export const personName = (people: ReadonlyMap<string, TeamPerson>, id: string | null | undefined, fallback = ''): string =>
    (id ? people.get(String(id))?.name : undefined) ?? fallback
